import { useEffect, useRef, useState } from "react";
import { Bell, X, Check, BellRing, BellOff } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { pushSupported, getCurrentSubscription, subscribePush, unsubscribePush } from "@/lib/push";

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [pushOn, setPushOn] = useState(false);
    const ref = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/notifications", { params: { limit: 30 } });
            setItems(data.items || []);
            setUnread(data.unread || 0);
        } catch {}
    };

    const refreshPushState = async () => {
        if (!pushSupported()) return;
        const sub = await getCurrentSubscription();
        setPushOn(!!sub && Notification.permission === "granted");
    };

    useEffect(() => {
        load();
        refreshPushState();
        const t = setInterval(load, 60_000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const markRead = async (id) => {
        await api.patch(`/notifications/${id}`, { read: true });
        setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        setUnread(u => Math.max(0, u - 1));
    };

    const remove = async (id) => {
        await api.delete(`/notifications/${id}`);
        const removed = items.find(n => n.id === id);
        setItems(prev => prev.filter(n => n.id !== id));
        if (removed && !removed.read) setUnread(u => Math.max(0, u - 1));
    };

    const markAll = async () => {
        await api.post("/notifications/read-all");
        setItems(prev => prev.map(n => ({ ...n, read: true })));
        setUnread(0);
    };

    const togglePush = async () => {
        try {
            if (pushOn) {
                await unsubscribePush();
                setPushOn(false);
                toast.success("reminders off");
            } else {
                await subscribePush();
                setPushOn(true);
                toast.success("reminders on. pax will gently knock when it's time.");
            }
        } catch (e) {
            toast.error(e?.message || "couldn't change reminders");
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                data-testid="notif-bell"
                onClick={() => { setOpen(o => !o); if (!open) load(); }}
                className="relative w-9 h-9 rounded-full hover:bg-clay flex items-center justify-center text-inkSoft hover:text-ink transition-colors"
                aria-label="Notifications"
            >
                <Bell className="w-4 h-4" />
                {unread > 0 && (
                    <span
                        data-testid="notif-badge"
                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-terracotta text-bone text-[10px] font-body rounded-full px-1 flex items-center justify-center"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notif-dropdown"
                    className="absolute right-0 mt-2 w-80 bg-white border border-clayHover rounded-2xl shadow-clay z-50 overflow-hidden"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-clayHover">
                        <span className="font-heading text-sm tracking-tight text-ink">notifications</span>
                        <div className="flex items-center gap-3">
                            {pushSupported() && (
                                <button
                                    data-testid="push-toggle"
                                    onClick={togglePush}
                                    title={pushOn ? "turn off reminders" : "enable gentle reminders"}
                                    className={`text-xs flex items-center gap-1 font-body ${pushOn ? "text-terracotta" : "text-inkSoft hover:text-ink"}`}
                                >
                                    {pushOn ? <BellRing className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                                    {pushOn ? "on" : "off"}
                                </button>
                            )}
                            {unread > 0 && (
                                <button
                                    data-testid="notif-mark-all"
                                    onClick={markAll}
                                    className="text-xs text-inkSoft hover:text-terracotta font-body"
                                >
                                    mark all read
                                </button>
                            )}
                        </div>
                    </div>
                    <ul className="max-h-80 overflow-y-auto" data-testid="notif-list">
                        {items.length === 0 && (
                            <li className="px-4 py-8 text-center text-inkSoft text-sm font-body italic">no notifications yet.</li>
                        )}
                        {items.map(n => (
                            <li
                                key={n.id}
                                data-testid={`notif-item-${n.id}`}
                                className={`group flex items-start gap-3 px-4 py-3 border-b border-clayHover/50 last:border-b-0 ${
                                    n.read ? "bg-white" : "bg-clay/40"
                                }`}
                            >
                                <div className="flex-1 min-w-0">
                                    {n.kind === "daily_checkin" && (
                                        <Link
                                            to="/companion"
                                            onClick={() => { markRead(n.id); setOpen(false); }}
                                            className="block"
                                        >
                                            <p className="font-body text-sm text-ink leading-snug">{n.title}</p>
                                            <p className="text-xs text-inkSoft mt-1 leading-relaxed">{n.body}</p>
                                        </Link>
                                    )}
                                    {n.kind !== "daily_checkin" && (
                                        <>
                                            <p className="font-body text-sm text-ink leading-snug">{n.title}</p>
                                            <p className="text-xs text-inkSoft mt-1 leading-relaxed">{n.body}</p>
                                        </>
                                    )}
                                    <p className="text-[10px] uppercase tracking-widest text-inkSoft mt-2">
                                        {new Date(n.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                    {!n.read && (
                                        <button onClick={() => markRead(n.id)} className="text-inkSoft hover:text-sage" aria-label="Mark read" data-testid={`notif-read-${n.id}`}>
                                            <Check className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    <button onClick={() => remove(n.id)} className="text-inkSoft hover:text-danger" aria-label="Dismiss" data-testid={`notif-del-${n.id}`}>
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
